import React, { useRef, useEffect } from 'react';

export const toHtml = (text: string): string =>
  text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>');

export const fromHtml = (html: string): string =>
  html
    .replace(/<strong>(.+?)<\/strong>/gi, '**$1**')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '');

export const injectVctStyles = () => {
  if (document.getElementById('vitamin-concept-test-styles')) return;
  const style = document.createElement('style');
  style.id = 'vitamin-concept-test-styles';
  style.textContent = `
    /* ═══ CSS Variables ═══ */
    :root, .vct-app {
      --teal: #07E5D1;
      --teal-bright: #0AF5E0;
      --teal-dark: #05A89B;
      --teal-soft: rgba(7, 229, 209, 0.06);
      --teal-glow: rgba(7, 229, 209, 0.12);
      --ink: #0a1f1c;
      --ink-soft: #2d4a45;
      --muted: #6b7f7c;
      --dim: #95a5a2;
      --bg: #f9fafb;
      --surface: #ffffff;
      --border: #e6ecea;
      --border-strong: #c7d6d3;
      --error: #dc2626;
      --error-soft: #fef2f2;
      --radius-sm: 8px;
      --radius: 12px;
      --radius-lg: 16px;
      --radius-xl: 22px;
      --shadow-sm: 0 1px 2px rgba(10, 31, 28, 0.04), 0 1px 3px rgba(10, 31, 28, 0.04);
      --shadow-md: 0 4px 14px rgba(10, 31, 28, 0.06), 0 2px 5px rgba(10, 31, 28, 0.04);
      --shadow-lg: 0 18px 38px rgba(10, 31, 28, 0.10), 0 6px 14px rgba(10, 31, 28, 0.05);
      --shadow-key: 0 1px 0 rgba(10, 31, 28, 0.20), inset 0 -1px 0 rgba(10, 31, 28, 0.05);
      --font-display: "Inter Tight", "Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
      --font-text: "Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
    }

    .vct-app {
      position: relative;
      min-height: 100vh;
      width: 100%;
      background: var(--bg);
      font-family: var(--font-text);
      color: var(--ink);
      -webkit-font-smoothing: antialiased;
    }

    /* ═══ Progress Bar ═══ */
    .vct-progress {
      position: fixed;
      top: 0; left: 0; right: 0;
      height: 4px;
      background: rgba(7, 229, 209, 0.08);
      z-index: 100;
    }
    .vct-progress__fill {
      height: 100%;
      width: 0;
      background: linear-gradient(90deg, var(--teal) 0%, var(--teal-bright) 100%);
      box-shadow: 0 0 12px var(--teal-glow);
      transition: width .55s cubic-bezier(.2,.8,.2,1);
    }

    /* ═══ Intro Banner ═══ */
    .vct-banner-wrap {
      padding: 64px 28px 0;
      display: flex;
      justify-content: center;
      position: relative;
      z-index: 30;
    }
    .vct-banner {
      width: 100%;
      max-width: 720px;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 14px;
      background: var(--teal-soft);
      border: 1px solid rgba(7, 229, 209, 0.28);
      border-left: 3px solid var(--teal);
      border-radius: 12px;
      box-shadow: var(--shadow-sm);
    }
    .vct-banner-enter {
      animation: vct-slideUp .45s cubic-bezier(.2,.8,.2,1) both;
    }
    .vct-banner__icon {
      flex-shrink: 0;
      width: 30px; height: 30px;
      border-radius: 50%;
      background: var(--surface);
      border: 1px solid rgba(7, 229, 209, 0.35);
      color: var(--teal);
      display: inline-flex; align-items: center; justify-content: center;
    }
    .vct-banner__icon svg { width: 15px; height: 15px; }
    .vct-banner__body {
      flex: 1;
      font-size: 13.5px;
      color: var(--ink-soft);
      line-height: 1.45;
    }
    .vct-banner__body strong {
      color: var(--ink);
      font-weight: 600;
    }
    .vct-banner__cta {
      flex-shrink: 0;
      background: var(--teal);
      color: white;
      border: none;
      padding: 7px 14px;
      border-radius: 7px;
      font-size: 13px; font-weight: 600;
      cursor: pointer;
      transition: background .18s;
    }
    .vct-banner__cta:hover { background: var(--teal-dark); }
    .vct-banner__close {
      flex-shrink: 0;
      width: 28px; height: 28px;
      border-radius: 6px;
      border: none;
      background: transparent;
      color: var(--muted);
      cursor: pointer;
      display: inline-flex; align-items: center; justify-content: center;
      transition: background .18s, color .18s;
    }
    .vct-banner__close:hover { background: rgba(0,0,0,.04); color: var(--ink); }
    .vct-banner__close svg { width: 14px; height: 14px; }
    .vct-app--banner .vct-screen { padding-top: 28px; }

    /* ═══ Top Bar ═══ */
    .vct-topbar {
      position: fixed; top: 0; left: 0; right: 0;
      display: flex; align-items: center; justify-content: space-between;
      padding: 18px 28px 14px;
      z-index: 40;
      pointer-events: none;
    }
    .vct-topbar > * { pointer-events: auto; }
    .vct-brand {
      display: inline-flex; align-items: center; gap: 10px;
      font-size: 13px; font-weight: 600; color: var(--ink-soft);
      white-space: nowrap;
    }
    .vct-brand__logo {
      display: inline-flex; align-items: center; gap: 7px;
      padding: 6px 11px;
      background: var(--ink);
      color: white;
      border-radius: 999px;
      font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
      text-transform: uppercase;
      white-space: nowrap;
      flex-shrink: 0;
    }
    .vct-brand__logo::before {
      content: "";
      width: 6px; height: 6px;
      border-radius: 50%;
      background: var(--teal-bright);
      box-shadow: 0 0 8px var(--teal-bright);
    }
    .vct-brand__sep { color: #d4dcdb; margin: 0 4px; }
    .vct-brand__title { color: var(--ink-soft); }
    .vct-exit-btn {
      border: 1px solid var(--border);
      background: rgba(255,255,255,.7);
      backdrop-filter: blur(8px);
      color: var(--ink-soft);
      padding: 8px 14px;
      border-radius: 8px;
      font-size: 13px; font-weight: 500;
      display: inline-flex; align-items: center; gap: 6px;
      transition: all .18s;
      cursor: pointer;
    }
    .vct-exit-btn:hover {
      border-color: var(--border-strong);
      color: var(--ink);
    }

    /* ═══ Screen Container ═══ */
    .vct-screen {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 100px 28px 140px;
      position: relative;
    }
    .vct-screen__inner {
      width: 100%;
      max-width: 720px;
    }
    .vct-screen-enter {
      animation: vct-slideUp .55s cubic-bezier(.2,.8,.2,1) both;
    }
    @keyframes vct-slideUp {
      from { opacity: 0; transform: translateY(28px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* ═══ Question Number ═══ */
    .vct-q-num {
      display: inline-flex; align-items: center; gap: 8px;
      color: var(--teal);
      font-size: 14px; font-weight: 600;
      margin-bottom: 18px;
      letter-spacing: 0.01em;
    }
    .vct-q-num svg { display: block; width: 11px; height: 11px; margin-left: 1px; }

    /* ═══ Headline ═══ */
    .vct-q-title {
      font-family: var(--font-display);
      font-size: clamp(28px, 4.2vw, 42px);
      font-weight: 600;
      line-height: 1.1;
      letter-spacing: -0.02em;
      color: var(--ink);
      margin: 0 0 14px;
    }
    .vct-q-title em {
      font-style: italic;
      color: var(--teal);
      font-weight: 500;
    }
    .vct-q-help {
      font-size: 17px;
      color: var(--muted);
      line-height: 1.55;
      margin: 0 0 36px;
      max-width: 60ch;
    }

    /* ═══ Big Input ═══ */
    .vct-big-input {
      width: 100%;
      border: none;
      border-bottom: 2px solid var(--border-strong);
      font-size: clamp(22px, 3vw, 30px);
      font-family: var(--font-display);
      font-weight: 400;
      padding: 12px 4px 14px;
      color: var(--ink);
      background: transparent;
      transition: border-color .2s;
      outline: none;
    }
    .vct-big-input::placeholder { color: #b6c2c0; font-weight: 300; }
    .vct-big-input:focus { border-bottom-color: var(--teal); }

    /* ═══ OK Button Row ═══ */
    .vct-ok-row {
      display: flex; align-items: center; gap: 16px;
      margin-top: 36px;
      flex-wrap: wrap;
    }
    .vct-ok-btn {
      display: inline-flex; align-items: center; gap: 10px;
      padding: 13px 22px;
      background: var(--teal);
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 16px; font-weight: 600;
      box-shadow: 0 4px 14px rgba(7, 229, 209, 0.30);
      transition: all .18s;
      cursor: pointer;
    }
    .vct-ok-btn:hover:not(:disabled) {
      background: var(--teal-dark);
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(7, 229, 209, 0.38);
    }
    .vct-ok-btn:disabled { opacity: .35; cursor: not-allowed; box-shadow: none; }
    .vct-ok-btn svg { width: 14px; height: 14px; }
    .vct-enter-hint {
      font-size: 13px;
      color: var(--muted);
      display: inline-flex; align-items: center; gap: 6px;
    }
    .vct-kbd {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 3px 7px;
      background: white;
      border: 1px solid var(--border-strong);
      border-radius: 5px;
      font-family: var(--font-display);
      font-size: 11px; font-weight: 600;
      color: var(--ink-soft);
      box-shadow: var(--shadow-key);
      line-height: 1;
    }

    /* ═══ Number Tiles ═══ */
    .vct-num-grid {
      display: flex; gap: 12px; flex-wrap: wrap;
      margin-bottom: 8px;
    }
    .vct-num-tile {
      width: 90px; height: 90px;
      border-radius: var(--radius-lg);
      border: 1.5px solid var(--border);
      background: var(--surface);
      font-family: var(--font-display);
      font-size: 32px; font-weight: 500;
      color: var(--ink);
      transition: all .18s cubic-bezier(.2,.8,.2,1);
      position: relative;
      overflow: hidden;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .vct-num-tile:hover {
      border-color: var(--border-strong);
      transform: translateY(-2px);
    }
    .vct-num-tile--selected {
      border-color: var(--teal);
      color: var(--teal);
      background: linear-gradient(135deg, var(--teal-soft) 0%, rgba(7, 229, 209, 0.02) 100%);
      box-shadow: 0 0 0 1px var(--teal), 0 12px 22px rgba(7, 229, 209, 0.16);
      transform: translateY(-2px);
    }
    .vct-num-tile__key {
      position: absolute;
      top: 6px; right: 8px;
      font-size: 10px; font-weight: 600;
      color: var(--dim);
      letter-spacing: 0.04em;
    }
    .vct-num-tile--selected .vct-num-tile__key { color: var(--teal); }

    .vct-mode-pill {
      display: inline-flex; align-items: center; gap: 10px;
      padding: 8px 14px;
      background: var(--teal-soft);
      border: 1px solid var(--border);
      border-radius: 999px;
      font-size: 13px;
      color: var(--ink-soft);
      margin-top: 18px;
    }
    .vct-mode-pill::before {
      content: "";
      width: 7px; height: 7px;
      border-radius: 50%;
      background: var(--teal);
      box-shadow: 0 0 0 4px var(--teal-glow);
    }

    /* ═══ Dropzone ═══ */
    .vct-dropzone {
      border: 2px dashed var(--border-strong);
      border-radius: var(--radius-lg);
      background: white;
      padding: 28px;
      text-align: center;
      transition: all .2s;
      cursor: pointer;
    }
    .vct-dropzone:hover, .vct-dropzone--drag {
      border-color: var(--teal);
      background: var(--teal-soft);
    }
    .vct-dropzone__icon {
      display: inline-flex; align-items: center; justify-content: center;
      width: 44px; height: 44px;
      border-radius: 11px;
      background: var(--teal-soft);
      color: var(--teal);
      margin-bottom: 12px;
    }
    .vct-dropzone__title {
      font-size: 16px; font-weight: 600;
      color: var(--ink); margin: 0 0 4px;
    }
    .vct-dropzone__title a {
      color: var(--teal); text-decoration: underline; text-underline-offset: 2px;
      cursor: pointer;
    }
    .vct-dropzone__hint {
      font-size: 13px; color: var(--muted);
    }

    .vct-split {
      display: flex; align-items: center;
      gap: 12px;
      margin: 18px 0;
      font-size: 12px; font-weight: 500;
      color: var(--dim); letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .vct-split::before, .vct-split::after {
      content: "";
      flex: 1; height: 1px;
      background: var(--border);
    }

    /* ═══ Requirements Hint ═══ */
    .vct-req-hint {
      margin: 12px 2px 18px;
      padding: 12px 14px;
      background: white;
      border: 1px solid var(--border);
      border-radius: 12px;
      font-size: 13px;
      line-height: 1.55;
      color: var(--ink-soft);
      display: flex;
      gap: 10px;
      align-items: flex-start;
    }
    .vct-req-hint__icon {
      flex-shrink: 0;
      width: 22px; height: 22px;
      border-radius: 6px;
      background: var(--teal-soft);
      color: var(--teal);
      display: inline-flex; align-items: center; justify-content: center;
      margin-top: 1px;
    }
    .vct-req-hint__body { flex: 1; min-width: 0; }
    .vct-req-hint__label {
      font-weight: 600;
      color: var(--ink);
      margin-bottom: 2px;
    }
    .vct-req-hint__list { color: var(--muted); }
    .vct-req-hint__list strong { color: var(--ink-soft); font-weight: 500; }
    .vct-req-hint__warn {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      margin-top: 6px;
      font-size: 12px;
      color: #b45309;
      font-weight: 500;
    }
    .vct-req-hint__warn::before {
      content: "";
      width: 5px; height: 5px;
      border-radius: 50%;
      background: #f59e0b;
      box-shadow: 0 0 0 3px rgba(245,158,11,.18);
    }

    /* ═══ See Sample - ANIMATED GRADIENT ═══ */
    .vct-see-sample {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      background: none;
      border: none;
      padding: 2px 0;
      margin-left: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      position: relative;
      background-image: linear-gradient(
        100deg,
        #07E5D1 0%,
        #0AF5E0 18%,
        #6366f1 38%,
        #ec4899 58%,
        #f59e0b 78%,
        #0AF5E0 92%,
        #07E5D1 100%
      );
      background-size: 280% 100%;
      background-position: 0% 50%;
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      animation: vct-hueShift 5s linear infinite;
    }
    .vct-see-sample::after {
      content: "→";
      -webkit-text-fill-color: transparent;
      background: inherit;
      -webkit-background-clip: text;
      background-clip: text;
      display: inline-block;
      transition: transform .25s cubic-bezier(.2,.8,.2,1);
    }
    .vct-see-sample:hover::after { transform: translateX(3px); }
    .vct-see-sample::before {
      content: "";
      position: absolute;
      left: 0; right: 0; bottom: 0;
      height: 1.5px;
      background-image: inherit;
      background-size: 280% 100%;
      background-position: inherit;
      border-radius: 2px;
      opacity: .5;
      animation: vct-hueShift 5s linear infinite;
    }
    @keyframes vct-hueShift {
      0%   { background-position: 0% 50%; }
      100% { background-position: 280% 50%; }
    }

    /* ═══ Modal ═══ */
    .vct-modal-overlay {
      position: fixed; inset: 0;
      background: rgba(10, 31, 28, 0.45);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      z-index: 200;
      display: flex; align-items: center; justify-content: center;
      padding: 28px;
      animation: vct-fadeIn .2s ease-out;
    }
    @keyframes vct-fadeIn { from { opacity: 0; } to { opacity: 1; } }
    .vct-modal {
      background: white;
      border-radius: 18px;
      max-width: 720px;
      width: 100%;
      max-height: 90vh;
      overflow: auto;
      box-shadow: 0 30px 80px rgba(10, 31, 28, .35);
      animation: vct-popIn .3s cubic-bezier(.2,1.2,.4,1);
    }
    @keyframes vct-popIn {
      from { opacity: 0; transform: translateY(12px) scale(.96); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    .vct-modal__head {
      display: flex; align-items: center; justify-content: space-between;
      padding: 18px 22px 14px;
      border-bottom: 1px solid var(--border);
    }
    .vct-modal__title {
      font-family: var(--font-display);
      font-size: 18px; font-weight: 600;
      margin: 0;
      color: var(--ink);
    }
    .vct-modal__close {
      background: none; border: none;
      padding: 6px; border-radius: 6px;
      color: var(--muted);
      display: inline-flex;
      cursor: pointer;
    }
    .vct-modal__close:hover { background: var(--bg); color: var(--ink); }
    .vct-modal__body { padding: 22px; }

    /* ═══ Sample Card (Bottle) ═══ */
    .vct-sample-card {
      background: white;
      border: 1px solid var(--border);
      border-radius: 14px;
      padding: 28px 24px;
      display: grid;
      grid-template-columns: 180px 1fr;
      gap: 28px;
      align-items: start;
    }
    .vct-sample-card__left {
      display: flex; flex-direction: column;
      align-items: center;
      gap: 18px;
    }
    .vct-sample-card__bottle {
      width: 150px; height: 200px;
      position: relative;
      filter: drop-shadow(0 18px 24px rgba(10,31,28,.12));
    }
    .vct-sample-card__bottle__cap {
      position: absolute; left: 50%; top: 0;
      transform: translateX(-50%);
      width: 76px; height: 22px;
      background: linear-gradient(180deg, #4ade80 0%, #16a34a 100%);
      border-radius: 8px 8px 4px 4px;
    }
    .vct-sample-card__bottle__body {
      position: absolute; left: 50%; top: 18px;
      transform: translateX(-50%);
      width: 130px;
      bottom: 0;
      background: linear-gradient(180deg, #fafaf9 0%, #f5f5f4 100%);
      border-radius: 10px;
      border: 1px solid #e7e5e4;
      display: flex; flex-direction: column;
      align-items: center;
      padding: 14px 8px 10px;
      overflow: hidden;
    }
    .vct-sample-card__bottle__brand {
      font-family: var(--font-display);
      font-weight: 800;
      font-size: 14px;
      color: #16a34a;
      line-height: 1;
      letter-spacing: -0.01em;
      text-align: center;
      margin-bottom: 10px;
    }
    .vct-sample-card__bottle__brand span { display: block; }
    .vct-sample-card__bottle__panel {
      width: 100%;
      background: white;
      border-radius: 6px;
      padding: 8px 6px;
      text-align: center;
      border: 1px solid #f3f4f6;
      flex: 1;
      display: flex; flex-direction: column;
      align-items: center;
      gap: 4px;
    }
    .vct-sample-card__bottle__kids {
      font-family: var(--font-display);
      font-weight: 800;
      font-size: 11px;
      color: #16a34a;
      line-height: 1;
    }
    .vct-sample-card__bottle__type {
      font-size: 7px;
      font-weight: 600;
      color: #525252;
      letter-spacing: 0.02em;
    }
    .vct-sample-card__bottle__gummies {
      display: flex; gap: 2px;
      margin-top: 2px;
    }
    .vct-sample-card__bottle__gummies span {
      width: 10px; height: 10px;
      border-radius: 50%;
    }
    .vct-sample-card__bottle__owl {
      width: 28px; height: 28px;
      border-radius: 50%;
      background: #1e3a8a;
      position: relative;
      margin-top: 2px;
    }
    .vct-sample-card__bottle__owl::before,
    .vct-sample-card__bottle__owl::after {
      content: "";
      position: absolute;
      top: 8px;
      width: 7px; height: 7px;
      border-radius: 50%;
      background: white;
    }
    .vct-sample-card__bottle__owl::before { left: 5px; }
    .vct-sample-card__bottle__owl::after { right: 5px; }
    .vct-sample-card__bottle__count {
      position: absolute; left: 50%; bottom: 8px;
      transform: translateX(-50%);
      font-size: 9px;
      color: #737373;
      font-weight: 600;
    }
    .vct-sample-card__price-block {
      text-align: center;
      font-size: 14px;
      color: var(--ink-soft);
      line-height: 1.5;
    }
    .vct-sample-card__price {
      font-size: 18px;
      font-weight: 600;
      color: var(--ink);
      font-family: var(--font-display);
    }
    .vct-sample-card__per {
      font-size: 13px;
      color: var(--muted);
      margin-top: 2px;
    }
    .vct-sample-card__pack {
      margin-top: 14px;
      font-size: 13px;
      color: var(--ink-soft);
      line-height: 1.5;
    }
    .vct-sample-card__right { padding-top: 14px; }
    .vct-sample-card__name {
      font-family: var(--font-display);
      font-size: 18px;
      font-weight: 700;
      color: var(--ink);
      letter-spacing: -0.01em;
      margin: 0 0 18px;
      line-height: 1.25;
    }
    .vct-sample-card__desc {
      font-size: 14px;
      color: var(--ink-soft);
      line-height: 1.6;
      margin: 0 0 14px;
    }
    .vct-sample-card__desc:last-child { margin-bottom: 0; }

    .vct-sample-legend {
      margin-top: 16px;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px 14px;
      font-size: 12px;
      color: var(--muted);
    }
    .vct-sample-legend__row { display: flex; align-items: center; gap: 6px; }
    .vct-sample-legend__dot {
      width: 7px; height: 7px;
      border-radius: 50%;
      background: var(--teal);
      flex-shrink: 0;
    }

    /* ═══ Sample Template (Text Format) ═══ */
    .vct-sample-template {
      background: linear-gradient(135deg, #fafaf9 0%, #f8f8f6 100%);
      border: 1px solid var(--border);
      border-radius: 12px;
      padding: 20px;
    }
    .vct-sample-template__row {
      display: flex;
      gap: 8px;
      padding: 10px 0;
      border-bottom: 1px solid var(--border-soft);
    }
    .vct-sample-template__row:last-child { border-bottom: none; }
    .vct-sample-template__row--block {
      flex-direction: column;
      gap: 6px;
    }
    .vct-sample-template__label {
      font-weight: 700;
      color: var(--teal);
      font-size: 14px;
      flex-shrink: 0;
    }
    .vct-sample-template__value {
      font-size: 14px;
      color: var(--ink-soft);
      line-height: 1.5;
    }
    .vct-sample-template__list {
      margin: 0;
      padding-left: 20px;
      font-size: 14px;
      color: var(--ink-soft);
      line-height: 1.7;
    }
    .vct-sample-template__list li {
      padding: 2px 0;
    }

    /* ═══ Sample Visual Card ═══ */
    .vct-sample-visual {
      background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
      border-radius: 16px;
      padding: 24px;
    }
    .vct-sample-visual__card {
      background: white;
      border: 1px solid var(--border);
      border-radius: 12px;
      display: grid;
      grid-template-columns: 180px 1fr;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0,0,0,.08);
    }
    .vct-sample-visual__left {
      background: linear-gradient(135deg, #f5f5f4 0%, #e8e8e6 100%);
      padding: 20px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 16px;
      border-right: 1px solid var(--border);
    }
    .vct-sample-visual__img {
      width: 120px;
      height: 120px;
      border-radius: 12px;
      background: white;
      border: 2px dashed var(--border);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      color: var(--muted);
      font-size: 11px;
      font-weight: 500;
    }
    .vct-sample-visual__price-box {
      text-align: center;
    }
    .vct-sample-visual__price {
      font-size: 20px;
      font-weight: 700;
      color: var(--ink);
      font-family: var(--font-display);
    }
    .vct-sample-visual__pack {
      font-size: 12px;
      color: var(--muted);
      margin-top: 2px;
    }
    .vct-sample-visual__right {
      padding: 20px 24px;
    }
    .vct-sample-visual__brand {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      color: var(--teal);
      margin-bottom: 6px;
    }
    .vct-sample-visual__headline {
      font-family: var(--font-display);
      font-size: 18px;
      font-weight: 700;
      color: var(--ink);
      margin: 0 0 8px;
      line-height: 1.3;
    }
    .vct-sample-visual__oneline {
      font-size: 13px;
      color: var(--ink-soft);
      margin: 0 0 12px;
      font-style: italic;
    }
    .vct-sample-visual__desc {
      font-size: 13px;
      color: var(--ink-soft);
      line-height: 1.6;
      margin: 0 0 14px;
    }
    .vct-sample-visual__rtb {
      background: var(--teal-soft);
      border-radius: 8px;
      padding: 12px 14px;
    }
    .vct-sample-visual__rtb-title {
      font-size: 11px;
      font-weight: 700;
      color: var(--teal);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 6px;
    }
    .vct-sample-visual__rtb ul {
      margin: 0;
      padding-left: 16px;
      font-size: 12px;
      color: var(--ink-soft);
      line-height: 1.6;
    }
    .vct-sample-visual__annotations {
      display: flex;
      flex-wrap: wrap;
      gap: 8px 16px;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid var(--border);
    }
    .vct-sample-visual__anno {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: var(--muted);
    }
    .vct-sample-visual__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--teal);
    }

    /* ═══ Concept Editor ═══ */
    .vct-concept-edit {
      border: 1.5px solid var(--border);
      border-radius: var(--radius-lg);
      background: white;
      overflow: hidden;
      transition: border-color .15s;
    }
    .vct-concept-edit:focus-within {
      border-color: var(--teal);
      box-shadow: 0 0 0 3px var(--teal-glow);
    }
    .vct-concept-edit__bar {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 12px;
      border-bottom: 1px solid var(--border);
      background: #fbfcfc;
    }
    .vct-tool-btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 6px 11px;
      background: white;
      border: 1px solid var(--border);
      border-radius: 7px;
      font-size: 12px; font-weight: 500;
      color: var(--ink-soft);
      transition: all .15s;
      cursor: pointer;
    }
    .vct-tool-btn:hover {
      border-color: var(--border-strong);
      background: var(--bg);
    }
    .vct-tool-btn svg { width: 13px; height: 13px; }
    .vct-concept-edit__textarea {
      width: 100%;
      min-height: 320px;
      padding: 16px 18px;
      border: none;
      outline: none;
      resize: vertical;
      font-family: var(--font-text);
      font-size: 14px;
      line-height: 1.65;
      color: var(--ink-soft);
      background: white;
    }
    .vct-concept-edit__textarea::placeholder { color: var(--dim); }
    .vct-concept-edit__textarea strong {
      font-weight: 600;
      color: var(--ink);
    }
    .vct-concept-edit__textarea:empty::before {
      content: attr(data-placeholder);
      color: var(--dim);
      pointer-events: none;
      white-space: pre-wrap;
    }

    /* ═══ Uploaded File ═══ */
    .vct-uploaded-file {
      display: flex; align-items: center; gap: 12px;
      padding: 12px 14px;
      background: white;
      border: 1px solid var(--border);
      border-radius: var(--radius);
      margin-bottom: 12px;
    }
    .vct-uploaded-file__thumb {
      width: 40px; height: 40px;
      border-radius: 8px;
      background: linear-gradient(135deg, #fef3c7, #fde68a);
      display: inline-flex; align-items: center; justify-content: center;
      font-size: 22px;
      flex-shrink: 0;
    }
    .vct-uploaded-file__name {
      flex: 1;
      font-size: 14px; font-weight: 500;
      color: var(--ink);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .vct-uploaded-file__meta {
      font-size: 12px;
      color: var(--muted);
    }
    .vct-uploaded-file__remove {
      background: none; border: none;
      color: var(--dim);
      padding: 4px; border-radius: 5px;
      display: inline-flex;
      cursor: pointer;
    }
    .vct-uploaded-file__remove:hover { color: var(--error); background: var(--error-soft); }

    /* ═══ Concept Tabs ═══ */
    .vct-concept-tabs {
      display: flex; gap: 8px; flex-wrap: wrap;
      margin-bottom: 22px;
    }
    .vct-concept-tab {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 8px 14px 8px 8px;
      background: white;
      border: 1.5px solid var(--border);
      border-radius: 999px;
      font-size: 13px; font-weight: 500;
      color: var(--muted);
      transition: all .15s;
      cursor: pointer;
    }
    .vct-concept-tab:hover { border-color: var(--border-strong); color: var(--ink-soft); }
    .vct-concept-tab--active {
      border-color: var(--teal);
      color: var(--teal);
      background: var(--teal-soft);
    }
    .vct-concept-tab--done { color: var(--ink); }
    .vct-concept-tab__num {
      display: inline-flex; align-items: center; justify-content: center;
      width: 22px; height: 22px;
      border-radius: 50%;
      background: var(--bg);
      border: 1px solid var(--border);
      font-size: 11px; font-weight: 600;
      color: var(--muted);
    }
    .vct-concept-tab--active .vct-concept-tab__num {
      background: var(--teal);
      border-color: var(--teal);
      color: white;
    }
    .vct-concept-tab--done .vct-concept-tab__num {
      background: var(--ink);
      border-color: var(--ink);
      color: white;
    }

    /* ═══ Choice Cards ═══ */
    .vct-choices {
      display: flex; flex-direction: column;
      gap: 12px;
      margin: 8px 0 0;
    }
    .vct-choice {
      display: flex; align-items: center; gap: 14px;
      padding: 14px 18px;
      background: var(--surface);
      border: 1.5px solid var(--border);
      border-radius: var(--radius);
      text-align: left;
      width: 100%;
      font-size: 17px;
      color: var(--ink);
      transition: all .15s;
      position: relative;
      cursor: pointer;
    }
    .vct-choice:hover {
      border-color: var(--teal);
      background: var(--teal-soft);
      transform: translateX(2px);
    }
    .vct-choice--selected {
      border-color: var(--teal);
      background: var(--teal-soft);
      box-shadow: 0 0 0 3px var(--teal-glow);
    }
    .vct-choice__key {
      display: inline-flex; align-items: center; justify-content: center;
      width: 26px; height: 26px;
      border-radius: 5px;
      border: 1.5px solid var(--border-strong);
      background: white;
      font-family: var(--font-display);
      font-size: 12px; font-weight: 600;
      color: var(--ink-soft);
      flex-shrink: 0;
    }
    .vct-choice--selected .vct-choice__key {
      background: var(--teal);
      border-color: var(--teal);
      color: white;
    }
    .vct-choice__label {
      flex: 1;
      font-weight: 500;
      line-height: 1.3;
    }
    .vct-choice__desc {
      font-size: 14px;
      color: var(--muted);
      font-weight: 400;
      margin-top: 3px;
      line-height: 1.4;
    }
    .vct-choice__meta {
      font-size: 13px;
      color: var(--muted);
      flex-shrink: 0;
    }
    .vct-choice__check {
      width: 20px; height: 20px;
      flex-shrink: 0;
      color: var(--teal);
      opacity: 0;
      transition: opacity .15s;
    }
    .vct-choice--selected .vct-choice__check { opacity: 1; }

    /* ═══ Segment / sub-segment selection ═══ */
    .vct-seg-loading {
      padding: 28px 0; text-align: center;
      font-size: 15px; color: var(--muted);
    }

    /* Custom checkbox */
    .vct-cbx-input { position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none; }
    .vct-cbx {
      flex-shrink: 0;
      width: 20px; height: 20px;
      border: 1.5px solid var(--border-strong, #c4d0cd);
      border-radius: 6px;
      background: white;
      display: inline-flex; align-items: center; justify-content: center;
      color: white;
      transition: background .15s, border-color .15s;
    }
    .vct-cbx svg { width: 13px; height: 13px; opacity: 0; transition: opacity .12s; }
    .vct-seg-card--checked .vct-cbx { background: var(--teal); border-color: var(--teal); }
    .vct-seg-card--checked .vct-cbx svg { opacity: 1; }

    /* Question header + select-all button */
    .vct-seg-header {
      display: flex; align-items: center; justify-content: space-between;
      gap: 16px; margin-bottom: 14px;
    }
    .vct-seg-header .vct-q-title { margin: 0; }
    .vct-seg-selectall {
      flex-shrink: 0;
      padding: 8px 16px;
      background: white;
      border: 1.5px solid var(--border);
      border-radius: 999px;
      font-size: 13px; font-weight: 600;
      color: var(--ink-soft);
      cursor: pointer;
      transition: all .15s;
    }
    .vct-seg-selectall:hover {
      border-color: var(--teal);
      background: var(--teal-soft);
      color: var(--teal-dark);
    }

    /* Segment list */
    .vct-seg-list { display: flex; flex-direction: column; gap: 10px; }
    .vct-seg-card {
      background: var(--surface);
      border: 1.5px solid var(--border);
      border-radius: var(--radius);
      overflow: hidden;
      transition: border-color .15s, box-shadow .15s;
    }
    .vct-seg-card--checked { border-color: var(--teal); }
    .vct-seg-card--narrowed { box-shadow: 0 0 0 3px var(--teal-glow); }
    .vct-seg-card__head {
      display: flex; align-items: center; gap: 14px;
      padding: 14px 18px;
      cursor: pointer;
      transition: background .15s;
    }
    .vct-seg-card__head:hover { background: var(--teal-soft); }
    .vct-seg-card__title { flex: 1; min-width: 0; }

    .vct-chips {
      display: flex; flex-wrap: wrap; gap: 8px;
      padding: 0 18px 16px 52px;
    }
    .vct-chip {
      display: inline-flex; align-items: center; gap: 7px;
      padding: 6px 12px;
      background: white;
      border: 1.5px solid var(--border);
      border-radius: 999px;
      font-size: 13px; font-weight: 500;
      color: var(--ink-soft);
      cursor: pointer;
      transition: all .15s;
    }
    .vct-chip:hover { border-color: var(--teal); background: var(--teal-soft); }
    .vct-chip--selected { border-color: var(--teal); background: var(--teal); color: white; }
    .vct-chip__count {
      font-size: 11px; font-weight: 600;
      padding: 1px 7px;
      border-radius: 999px;
      background: rgba(10, 31, 28, 0.06);
      color: var(--muted);
    }
    .vct-chip--selected .vct-chip__count {
      background: rgba(255, 255, 255, 0.25);
      color: white;
    }
    .vct-seg-summary {
      margin-top: 18px;
      padding: 12px 16px;
      background: var(--teal-soft);
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
      font-size: 14px; font-weight: 500;
      color: var(--ink-soft);
      text-align: center;
    }

    /* ═══ Review Card ═══ */
    .vct-review-card {
      background: white;
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      overflow: hidden;
      margin-bottom: 20px;
    }
    .vct-review-row {
      display: flex; align-items: flex-start; gap: 18px;
      padding: 16px 22px;
      border-bottom: 1px solid var(--border);
    }
    .vct-review-row:last-child { border-bottom: none; }
    .vct-review-row__label {
      font-size: 13px;
      color: var(--muted);
      width: 140px;
      flex-shrink: 0;
      padding-top: 2px;
      font-weight: 500;
    }
    .vct-review-row__value {
      flex: 1;
      font-size: 15px;
      color: var(--ink);
      font-weight: 500;
      line-height: 1.45;
      min-width: 0;
    }
    .vct-review-row__edit {
      background: none; border: none;
      font-size: 13px;
      color: var(--teal);
      display: inline-flex; align-items: center; gap: 4px;
      padding: 4px 8px; border-radius: 6px;
      transition: background .15s;
      flex-shrink: 0;
      cursor: pointer;
    }
    .vct-review-row__edit:hover { background: var(--teal-soft); }
    .vct-concept-chip {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 4px 10px 4px 4px;
      background: var(--bg);
      border: 1px solid var(--border);
      border-radius: 999px;
      font-size: 13px;
      color: var(--ink-soft);
      margin: 3px 4px 3px 0;
    }
    .vct-concept-chip__num {
      display: inline-flex; align-items: center; justify-content: center;
      width: 20px; height: 20px;
      border-radius: 50%;
      background: var(--teal);
      color: white;
      font-size: 11px; font-weight: 600;
    }

    /* ═══ Done / Loading Screen ═══ */
    .vct-done-screen {
      text-align: center;
      display: flex; flex-direction: column; align-items: center;
      gap: 18px;
    }
    .vct-done-icon {
      width: 88px; height: 88px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--teal) 0%, var(--teal-bright) 100%);
      color: white;
      display: inline-flex; align-items: center; justify-content: center;
      box-shadow: 0 18px 40px rgba(7, 229, 209, 0.30);
      margin-bottom: 8px;
      animation: vct-pop .55s cubic-bezier(.2, 1.4, .4, 1) .1s both;
    }
    @keyframes vct-pop {
      from { transform: scale(0); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }
    .vct-done-icon svg { width: 38px; height: 38px; }
    .vct-done-title {
      font-family: var(--font-display);
      font-size: clamp(30px, 4vw, 44px);
      font-weight: 600;
      letter-spacing: -0.02em;
      color: var(--ink);
      margin: 0;
    }
    .vct-done-subtitle {
      font-size: 17px;
      color: var(--muted);
      margin: 0;
      max-width: 50ch;
      line-height: 1.55;
    }
    .vct-done-meta {
      display: inline-flex; align-items: center; gap: 24px;
      padding: 14px 22px;
      background: white;
      border: 1px solid var(--border);
      border-radius: 12px;
      margin-top: 14px;
    }
    .vct-done-meta__item {
      display: flex; flex-direction: column; gap: 2px;
    }
    .vct-done-meta__num {
      font-family: var(--font-display);
      font-size: 24px; font-weight: 600;
      color: var(--ink);
      line-height: 1;
    }
    .vct-done-meta__label {
      font-size: 11px;
      color: var(--muted);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-weight: 500;
    }
    .vct-done-meta__sep {
      width: 1px;
      align-self: stretch;
      background: var(--border);
    }
    .vct-spinner {
      width: 38px; height: 38px;
      border: 3px solid var(--border);
      border-top-color: var(--teal);
      border-radius: 50%;
      animation: vct-spin 0.8s linear infinite;
    }
    @keyframes vct-spin { to { transform: rotate(360deg); } }
    .vct-loading-bar {
      width: 100%; max-width: 320px;
      height: 6px;
      background: var(--bg);
      border-radius: 3px;
      overflow: hidden;
      margin-top: 18px;
    }
    .vct-loading-bar__fill {
      height: 100%;
      background: linear-gradient(90deg, var(--teal), var(--teal-bright));
      border-radius: 3px;
      width: 0;
      transition: width .3s;
    }

    /* ═══ Step Nav ═══ */
    .vct-step-nav {
      position: fixed;
      bottom: 24px; right: 24px;
      display: flex; align-items: center; gap: 14px;
      z-index: 50;
    }
    .vct-step-counter {
      font-size: 13px;
      color: var(--muted);
    }
    .vct-step-counter strong {
      color: var(--ink);
      font-family: var(--font-display);
      font-weight: 600;
    }
    .vct-step-nav__btns {
      display: flex;
      background: var(--ink);
      border-radius: 8px;
      overflow: hidden;
      box-shadow: var(--shadow-md);
    }
    .vct-step-nav__btn {
      width: 38px; height: 38px;
      background: transparent;
      border: none;
      color: white;
      display: inline-flex; align-items: center; justify-content: center;
      transition: background .15s;
      cursor: pointer;
    }
    .vct-step-nav__btn:hover:not(:disabled) {
      background: rgba(255,255,255, 0.10);
    }
    .vct-step-nav__btn:disabled {
      opacity: .3;
      cursor: not-allowed;
    }
    .vct-step-nav__btn + .vct-step-nav__btn {
      border-left: 1px solid rgba(255,255,255,.15);
    }
    .vct-step-nav__btn svg { width: 14px; height: 14px; }

    /* ═══ Admin Panel ═══ */
    .vct-admin-panel {
      margin-top: 24px;
      padding: 16px;
      background: white;
      border: 1px solid var(--border);
      border-radius: var(--radius);
    }
    .vct-admin-panel__toggle {
      display: flex; align-items: center; gap: 8px;
      width: 100%;
      padding: 8px 0;
      background: none;
      border: none;
      color: var(--muted);
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: color .15s;
    }
    .vct-admin-panel__toggle:hover { color: var(--ink); }
    .vct-admin-panel__content {
      padding-top: 12px;
      display: flex; flex-direction: column; gap: 12px;
    }
    .vct-admin-field {
      display: flex; flex-direction: column; gap: 4px;
    }
    .vct-admin-field label {
      font-size: 12px;
      font-weight: 500;
      color: var(--ink-soft);
    }
    .vct-admin-field input,
    .vct-admin-field select {
      padding: 8px 12px;
      border: 1px solid var(--border);
      border-radius: 6px;
      font-size: 14px;
      color: var(--ink);
      background: white;
      outline: none;
    }
    .vct-admin-field input:focus,
    .vct-admin-field select:focus {
      border-color: var(--teal);
    }

    /* ═══ Concept Grid ═══ */
    .vct-concept-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      gap: 14px;
      margin: 14px 0 0;
    }
    .vct-concept-card {
      position: relative;
      background: white;
      border: 1.5px solid var(--border);
      border-radius: 14px;
      overflow: hidden;
      aspect-ratio: 3 / 4;
      transition: all .18s;
      display: flex; flex-direction: column;
      cursor: default;
    }
    .vct-concept-card:hover {
      border-color: var(--border-strong);
      transform: translateY(-2px);
      box-shadow: var(--shadow-md);
    }
    .vct-concept-card__thumb {
      position: relative;
      flex: 1;
      background: linear-gradient(135deg, #f5f5f4 0%, #eaeae8 100%);
      display: flex; align-items: center; justify-content: center;
      font-size: 38px;
      overflow: hidden;
      position: relative;
    }
    .vct-concept-card__thumb img {
      width: 100%; height: 100%; object-fit: cover;
      display: block;
    }
    .vct-concept-card__thumb--text {
      background: linear-gradient(135deg, var(--teal-soft), rgba(7,229,209,.02));
      color: var(--teal);
    }
    .vct-concept-card__meta {
      padding: 9px 12px;
      border-top: 1px solid var(--border);
      background: white;
    }
    .vct-concept-card__num {
      font-size: 10px; font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--teal);
      margin-bottom: 2px;
    }
    .vct-concept-card__name {
      font-size: 12px;
      font-weight: 600;
      color: var(--ink);
      line-height: 1.3;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .vct-concept-card__source {
      position: absolute;
      top: 8px; left: 8px;
      background: rgba(255,255,255,.92);
      backdrop-filter: blur(4px);
      border-radius: 6px;
      padding: 3px 7px;
      font-size: 10px; font-weight: 600;
      color: var(--ink-soft);
      display: inline-flex; align-items: center; gap: 4px;
    }
    .vct-concept-card__source svg { width: 11px; height: 11px; }
    .vct-concept-card__remove {
      position: absolute;
      top: 8px; right: 8px;
      z-index: 5;
      width: 26px; height: 26px;
      border-radius: 50%;
      background: rgba(239,68,68,.9);
      backdrop-filter: blur(4px);
      color: white;
      border: none;
      display: inline-flex; align-items: center; justify-content: center;
      opacity: 0;
      transition: opacity .15s, background .15s, transform .15s;
      cursor: pointer;
    }
    .vct-concept-card:hover .vct-concept-card__remove { opacity: 1; }
    .vct-concept-card__remove:hover { background: var(--error); transform: scale(1.1); }
    .vct-concept-card__remove svg { width: 13px; height: 13px; }

    /* Hover hint overlay */
    .vct-concept-card__hover-hint {
      position: absolute;
      inset: 0;
      background: rgba(7,229,209,.65);
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      gap: 6px;
      opacity: 0;
      transition: opacity .2s;
      pointer-events: none;
      z-index: 2;
    }
    .vct-concept-card:hover .vct-concept-card__hover-hint { opacity: 1; }
    .vct-concept-card__hover-hint svg { width: 22px; height: 22px; color: white; }
    .vct-concept-card__hover-hint span {
      font-size: 11px; font-weight: 600;
      color: white;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .vct-concept-card--parsing .vct-concept-card__thumb::after {
      content: "";
      position: absolute; inset: 0;
      background: rgba(255,255,255,.7);
      backdrop-filter: blur(2px);
    }
    .vct-concept-card--parsing::before {
      content: "";
      position: absolute;
      top: calc(50% - 13px);
      left: calc(50% - 13px);
      z-index: 10;
      width: 26px; height: 26px;
      border: 3px solid rgba(7,229,209,.2);
      border-top-color: var(--teal);
      border-radius: 50%;
      animation: vct-spin .8s linear infinite;
    }
    .vct-concept-card__done {
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: 48px; height: 48px;
      border-radius: 50%;
      background: #10B981;
      color: white;
      display: inline-flex; align-items: center; justify-content: center;
      box-shadow: 0 4px 14px rgba(16, 185, 129, 0.35), 0 0 0 4px rgba(16, 185, 129, 0.15);
      pointer-events: none;
      z-index: 4;
      animation: vct-done-pop .5s cubic-bezier(.2, 1.6, .4, 1) both;
    }
    .vct-concept-card__done svg { width: 24px; height: 24px; stroke-width: 3; }
    @keyframes vct-done-pop {
      0%   { transform: translate(-50%, -50%) scale(0); opacity: 0; }
      60%  { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
      100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
    }
    .vct-concept-card:hover .vct-concept-card__done { opacity: 0; transition: opacity .2s; }

    /* ═══ Add Tile ═══ */
    .vct-add-tile {
      background: white;
      border: 1.5px dashed var(--border-strong);
      border-radius: 14px;
      aspect-ratio: 3 / 4;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      gap: 6px;
      color: var(--muted);
      transition: all .18s;
      cursor: pointer;
      font-size: 12px;
      font-weight: 500;
    }
    .vct-add-tile:hover {
      border-color: var(--teal);
      color: var(--teal);
      background: var(--teal-soft);
    }
    .vct-add-tile__plus {
      width: 36px; height: 36px;
      border-radius: 50%;
      background: var(--teal-soft);
      color: var(--teal);
      display: inline-flex; align-items: center; justify-content: center;
    }
    .vct-add-tile__plus svg { width: 18px; height: 18px; }

    /* ═══ Secondary Button ═══ */
    .vct-secondary-btn {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 16px 22px;
      background: white;
      border: 1.5px solid var(--border);
      border-radius: 14px;
      cursor: pointer;
      text-align: left;
      transition: all .18s;
      font-family: var(--font-text);
    }
    .vct-secondary-btn:hover {
      border-color: var(--teal);
      background: var(--teal-soft);
      transform: translateY(-1px);
    }
    .vct-secondary-btn__icon {
      width: 40px; height: 40px;
      flex-shrink: 0;
      border-radius: 10px;
      background: var(--teal-soft);
      color: var(--teal);
      display: inline-flex; align-items: center; justify-content: center;
    }
    .vct-secondary-btn__icon svg { width: 18px; height: 18px; }
    .vct-secondary-btn__title {
      font-size: 15px;
      font-weight: 600;
      color: var(--ink);
      line-height: 1.3;
      margin-bottom: 2px;
    }
    .vct-secondary-btn__sub {
      font-size: 13px;
      color: var(--muted);
    }
    .vct-secondary-btn__body { flex: 1; min-width: 0; }
    .vct-secondary-btn__arrow {
      color: var(--muted);
      flex-shrink: 0;
      transition: transform .2s, color .2s;
    }
    .vct-secondary-btn:hover .vct-secondary-btn__arrow {
      transform: translateX(3px);
      color: var(--teal);
    }
    .vct-secondary-btn__arrow svg { width: 16px; height: 16px; }

    /* ═══ OR Divider ═══ */
    .vct-or-divider {
      display: flex; align-items: center; gap: 14px;
      margin: 20px 0;
      font-size: 12px; font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--muted);
      white-space: nowrap;
    }
    .vct-or-divider::before, .vct-or-divider::after {
      content: "";
      flex: 1;
      height: 1px;
      background: var(--border);
    }

    /* ═══ Requirements Line ═══ */
    .vct-req-line {
      margin: 10px 2px 0;
      font-size: 13px;
      color: var(--muted);
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      line-height: 1.5;
    }
    .vct-req-line strong {
      color: var(--ink-soft);
      font-weight: 500;
    }
    .vct-req-line__sep {
      display: inline-block;
      width: 3px; height: 3px;
      border-radius: 50%;
      background: var(--border-strong);
      margin: 0 2px;
    }

    /* ═══ Manual Editor Modal ═══ */
    .vct-modal--editor {
      max-width: 800px;
    }
    .vct-modal--editor .vct-modal__body {
      padding: 0;
    }
    .vct-manual-modal__eyebrow {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--muted);
    }
    .vct-manual-modal__textarea {
      width: 100%;
      min-height: 400px;
      padding: 22px;
      border: none;
      font-family: var(--font-text);
      font-size: 15px;
      line-height: 1.65;
      color: var(--ink);
      resize: vertical;
      background: var(--bg);
      outline: none;
    }
    .vct-manual-modal__textarea::placeholder { color: var(--dim); }
    .vct-modal__foot {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 22px;
      border-top: 1px solid var(--border);
      background: white;
    }
    .vct-tool-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 12px;
      background: white;
      border: 1px solid var(--border);
      border-radius: 8px;
      font-size: 13px;
      font-weight: 500;
      color: var(--ink-soft);
      cursor: pointer;
      transition: all .15s;
    }
    .vct-tool-btn:hover {
      border-color: var(--border-strong);
      color: var(--ink);
    }
    .vct-tool-btn svg { width: 14px; height: 14px; }

    @media (max-width: 640px) {
      .vct-topbar { padding: 14px 18px 10px; }
      .vct-screen { padding: 90px 22px 130px; }
      .vct-num-tile { width: 72px; height: 72px; font-size: 26px; }
      .vct-review-row { flex-direction: column; gap: 6px; padding: 14px 18px; }
      .vct-review-row__label { width: auto; }
      .vct-sample-card {
        grid-template-columns: 1fr;
        gap: 18px;
        text-align: center;
      }
      .vct-sample-card__right { padding-top: 0; }
    }
  `;
  document.head.appendChild(style);
};

const IconX = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);
const IconFileText = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
  </svg>
);
const IconClear = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export const CONCEPT_TEMPLATE = `**Product Headline:** Your Product Name

**Brand Name:** Your Brand Name

**One Line Description:** A brief statement about the problem your product solves or the need it addresses.

**Detailed Description:** Describe your product in detail. What makes it unique? Who is it for? What benefits does it provide? Include key features, ingredients, or specifications.

**Reasons to Believe:**
- First key claim or benefit
- Second key claim or benefit
- Third key claim or benefit
- Fourth key claim or benefit (optional)

**Price & Pack:** $XX.XX for XX units`;

export const ConceptSampleModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  useEffect(() => { injectVctStyles(); }, []);
  return (
    <div className="vct-modal-overlay" onClick={onClose}>
      <div className="vct-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="vct-modal__head">
          <h3 className="vct-modal__title">Sample concept card</h3>
          <button className="vct-modal__close" onClick={onClose}><IconX /></button>
        </div>
        <div className="vct-modal__body">
          <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.5 }}>
            Your concept card should look something like this. Upload an image or write manually.
          </p>
          <div className="vct-sample-visual">
            <div className="vct-sample-visual__card">
              <div className="vct-sample-visual__left">
                <div className="vct-sample-visual__img">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 48, height: 48, opacity: 0.4 }}>
                    <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" />
                  </svg>
                  <span>Product Image</span>
                </div>
                <div className="vct-sample-visual__price-box">
                  <div className="vct-sample-visual__price">$24.99</div>
                  <div className="vct-sample-visual__pack">for 30 units</div>
                </div>
              </div>
              <div className="vct-sample-visual__right">
                <div className="vct-sample-visual__brand">BRAND NAME</div>
                <h4 className="vct-sample-visual__headline">Product Headline Goes Here</h4>
                <p className="vct-sample-visual__oneline">A brief statement about the problem your product solves.</p>
                <p className="vct-sample-visual__desc">Detailed description of your product. What makes it unique? Who is it for? Include key features and benefits that set it apart.</p>
                <div className="vct-sample-visual__rtb">
                  <div className="vct-sample-visual__rtb-title">Key Benefits:</div>
                  <ul>
                    <li>First key claim or benefit</li>
                    <li>Second key claim or benefit</li>
                    <li>Third key claim or benefit</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="vct-sample-visual__annotations">
              <div className="vct-sample-visual__anno"><span className="vct-sample-visual__dot" />Product image</div>
              <div className="vct-sample-visual__anno"><span className="vct-sample-visual__dot" />Brand name</div>
              <div className="vct-sample-visual__anno"><span className="vct-sample-visual__dot" />Product headline</div>
              <div className="vct-sample-visual__anno"><span className="vct-sample-visual__dot" />Description</div>
              <div className="vct-sample-visual__anno"><span className="vct-sample-visual__dot" />Claims / RTBs</div>
              <div className="vct-sample-visual__anno"><span className="vct-sample-visual__dot" />Price &amp; pack</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ConceptManualModalProps {
  initialValue: string;
  onSave: (value: string) => void;
  onClose: () => void;
}

export const ConceptManualModal: React.FC<ConceptManualModalProps> = ({ initialValue, onSave, onClose }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastValue = useRef(initialValue);
  const [charCount, setCharCount] = React.useState(initialValue.length);

  useEffect(() => { injectVctStyles(); }, []);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = toHtml(initialValue);
      lastValue.current = initialValue;
      editorRef.current.focus();
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }, []); // run once on mount

  const resetToTemplate = () => {
    lastValue.current = CONCEPT_TEMPLATE;
    setCharCount(CONCEPT_TEMPLATE.length);
    if (editorRef.current) editorRef.current.innerHTML = toHtml(CONCEPT_TEMPLATE);
  };

  const clear = () => {
    lastValue.current = '';
    setCharCount(0);
    if (editorRef.current) editorRef.current.innerHTML = '';
  };

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const text = fromHtml((e.target as HTMLDivElement).innerHTML);
    lastValue.current = text;
    setCharCount(text.length);
  };

  const handleSave = () => onSave(lastValue.current);

  return (
    <div className="vct-modal-overlay" onClick={onClose}>
      <div className="vct-modal vct-modal--editor" onClick={e => e.stopPropagation()}>
        <div className="vct-modal__head">
          <div>
            <div className="vct-manual-modal__eyebrow">CONCEPT</div>
            <h2 className="vct-modal__title">Write your concept</h2>
          </div>
          <button className="vct-modal__close" onClick={onClose}><IconX /></button>
        </div>
        <div className="vct-modal__body">
          <div style={{ display: 'flex', gap: 8, padding: '12px 22px', borderBottom: '1px solid var(--border)', background: 'white' }}>
            <button className="vct-tool-btn" type="button" onClick={resetToTemplate}>
              <IconFileText /> Reset to template
            </button>
            <button className="vct-tool-btn" type="button" onClick={clear}>
              <IconClear /> Clear
            </button>
          </div>
          <div
            ref={editorRef}
            className="vct-manual-modal__textarea vct-concept-edit__textarea"
            contentEditable
            suppressContentEditableWarning
            data-placeholder={`**Product Headline:** Your product name\n\n**Brand Name:** Your brand\n\n**One Line Description:** What problem does it solve?\n\n**Detailed Description:** Tell the full story...`}
            onInput={handleInput}
            style={{ minHeight: 400, outline: 'none' }}
          />
        </div>
        <div className="vct-modal__foot">
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>{charCount} characters</span>
          <div style={{ flex: 1 }} />
          <button className="vct-ok-btn" type="button" onClick={handleSave} disabled={charCount === 0}>
            Save concept
          </button>
        </div>
      </div>
    </div>
  );
};
